import type {
  Subscription,
  SubscriptionChangePreview,
} from '@repo/subscription/api';
import { capitalizeFirstLetter, pluralSuffix } from '@repo/ui/utils';

import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { useUserStore, userStoreSelector } from '../../../stores/userStore';
import { formatMonetaryValue, getYearlyCharge } from '../../../utils/billing';
import { getDateLabel } from '../utils';

import { PaymentInfoSectionParams, PaymentInfoSectionData } from './types';
import usePlanPrice from './usePlanPrice';

const getQuantity = (
  currentSubscription?: Subscription | null,
  changeSubscriptionPreview?: SubscriptionChangePreview | null
): number => {
  if (
    changeSubscriptionPreview?.plan &&
    'quantity' in changeSubscriptionPreview.plan
  ) {
    return changeSubscriptionPreview.plan.quantity;
  }
  if (currentSubscription?.plan && 'quantity' in currentSubscription.plan) {
    return currentSubscription.plan.quantity;
  }
  return 1;
};

const usePaymentInfoPlanSection = ({
  currentSubscription,
  changeSubscriptionPreview,
}: PaymentInfoSectionParams): PaymentInfoSectionData => {
  const isTeam = useUserStore(userStoreSelector.isTeam);
  const selectedPlan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const isYearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);

  const planPrice = usePlanPrice({
    plan: selectedPlan,
    isYearly,
  });
  const currency = planPrice.currency;
  const planCharge = isYearly ? getYearlyCharge(planPrice) : planPrice;

  const period = isYearly ? 'year' : 'month';

  const hasExistingCredit =
    currentSubscription && (currentSubscription.balance ?? 0) < 0;
  // NOTE: credit is only be given on a change from pro/yearly to expert/monthly
  const hasProratedCredit = !!(
    changeSubscriptionPreview?.amountDueNow &&
    changeSubscriptionPreview.amountDueNow < 0
  );

  const pendingPlanChange = !!(
    currentSubscription?.pendingPlan?.name &&
    currentSubscription.pendingPlan.name !== currentSubscription.plan?.name
  );

  const hasPendingChange =
    currentSubscription?.pendingPlan && pendingPlanChange;

  const currentPlan = currentSubscription?.plan;

  const selectedQuantity = currentPlan
    ? getQuantity(currentSubscription, changeSubscriptionPreview)
    : quantity;

  const planChargeWithQuantity = {
    ...planCharge,
    amount: planCharge.amount * quantity,
  };

  const currentCredit = {
    amount: currentSubscription?.balance ?? 0,
    currency,
  } as const;

  const proratedCredit = {
    amount: changeSubscriptionPreview?.amountDueNow ?? 0,
    currency,
  } as const;

  const nextInvoiceAmount = {
    amount: currentSubscription?.nextInvoiceAmount ?? 0,
    currency,
  } as const;

  const result: PaymentInfoSectionData = [
    {
      leftText: `${
        isTeam
          ? `${formatMonetaryValue(planCharge, { fractionDigits: 0 })} `
          : ''
      }${capitalizeFirstLetter(selectedPlan.toLowerCase())} plan`,
      rightText: `${formatMonetaryValue(planChargeWithQuantity)}/${period}`,
    },
  ];

  if (isTeam) {
    result.push({
      leftText: `${selectedQuantity} Member${pluralSuffix(
        selectedQuantity
      )} × 1 ${capitalizeFirstLetter(period)}`,
    });
  }

  if (hasExistingCredit) {
    result.push({
      leftText: 'Your current credit',
      rightText: formatMonetaryValue(currentCredit),
    });
  }

  if (hasProratedCredit) {
    result.push({
      leftText: 'Prorated credit from your current plan',
      rightText: formatMonetaryValue(proratedCredit),
    });
  }

  if (hasPendingChange) {
    result.push({
      leftText: `Changing to ${capitalizeFirstLetter(
        currentSubscription?.pendingPlan?.name?.toLowerCase() ?? ''
      )} on ${getDateLabel(currentSubscription?.currentPeriodEnd)}`,
      rightText: formatMonetaryValue(nextInvoiceAmount),
    });
  }

  return result;
};

export default usePaymentInfoPlanSection;
